import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';

interface Testimonial {
  text: string;
}

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const RAW_TESTIMONIALS: Testimonial[] = [
  { text: 'La capacitación fue muy dinámica, con contenidos prácticos, útiles y precisos para la tarea que estoy desempeñando actualmente. El desenvolvimiento del capacitador fue excelente, no dejó dudas en ninguna clase. ¡Gracias por esta oportunidad de aprender algo nuevo!' },
  { text: 'Aprendimos mediante trabajos individuales y grupales que permitían la interacción y el intercambio de experiencias con nuestros compañeros.' },
  { text: 'He sido Analista durante años en compañías nacionales como multinacionales, y adquirí nuevos conocimientos que antes jamás me habían mencionado. ¡Excelente oportunidad para seguir creciendo!' },
  { text: 'La variedad de temas y la predisposición del profesor hacen que el programa sea abierto para todos; no te ves limitado por el área a la que te dediques, seguro te va a servir y vas a poder aplicarlo en la vida laboral.' },
  { text: 'Muy claro en sus explicaciones. Se nota que sabe mucho del tema y lo transmitió a lo largo del programa.' },
  { text: 'Alejandro fue muy dedicado y organizado, permitiendo que todos se sintieran cómodos de hacer consultas y respondió todas las dudas' },
  { text: 'Como docente, ¡noto una muy buena didáctica del parte del Profesor para explicar los temas que no siempre es fácil en estas áreas, muy contenta con el curso!' },
  { text: 'Fue muy profesional, amable y proactivo a la hora de responder dudas y brindar contenido extra. Muy claras las explicaciones con ejemplos incluidos.' },
  { text: '¡La verdad un genio! ¡Muy claro en las explicaciones y dinámico! ¡No me aburrí nunca!' },
];

export default function TestimonialsCompact() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const testimonials = useMemo(() => shuffleArray(RAW_TESTIMONIALS), []);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning]);

  const handlePrevious = useCallback(() => {
    if (!isTransitioning) {
      const newIndex = currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1;
      goToSlide(newIndex);
    }
  }, [currentIndex, isTransitioning, testimonials.length, goToSlide]);

  const handleNext = useCallback(() => {
    if (!isTransitioning) {
      const newIndex = currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1;
      goToSlide(newIndex);
    }
  }, [currentIndex, isTransitioning, testimonials.length, goToSlide]);

  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => {
          setIsTransitioning(true);
          setTimeout(() => setIsTransitioning(false), 500);
          return (prev + 1) % testimonials.length;
        });
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [isPaused, testimonials.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && !isTransitioning) handlePrevious();
      else if (e.key === 'ArrowRight' && !isTransitioning) handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isTransitioning, handlePrevious, handleNext]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) handleNext();
      else handlePrevious();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <div className="h-full flex flex-col justify-center">
      {/* Heading */}
      <div className="mb-4 md:mb-5">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-black mb-2 tracking-tight leading-tight">
          Lo que dicen quienes ya se capacitaron
        </h2>
        <p className="text-sm md:text-base text-neutral-600 leading-relaxed">
          Más de 4.000 profesionales ya aplican estas metodologías para tomar mejores decisiones con datos e IA.
        </p>
      </div>

      {/* Star rating + testimonial card side by side */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 md:gap-4">
        {/* Rating card */}
        <div className="sm:col-span-2 bg-neutral-50 border border-neutral-200 rounded-xl p-4 md:p-5 flex flex-col items-center justify-center text-center">
          <Star className="w-8 h-8 md:w-10 md:h-10 fill-[#C5075D] text-[#C5075D] mb-2" />
          <div className="text-2xl md:text-3xl font-bold text-black">
            4.91 <span className="text-neutral-400 text-lg md:text-xl">/ 5</span>
          </div>
          <p className="text-xs md:text-sm text-neutral-500 mt-1">
            300+ evaluaciones
          </p>
        </div>

        {/* Testimonial carousel */}
        <div
          className="sm:col-span-3 relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="bg-white border border-neutral-200 rounded-xl p-4 md:p-5 shadow-sm relative overflow-hidden flex flex-col min-h-[140px]">
            <Quote className="w-7 h-7 md:w-8 md:h-8 text-[#C5075D] opacity-40 absolute top-3 left-3 md:top-4 md:left-4" />

            <div className="flex-1 flex items-center pt-8">
              <div className="w-full transition-opacity duration-500">
                <p className="text-neutral-800 text-sm md:text-base leading-relaxed">
                  {testimonials[currentIndex].text}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 mt-4">
              <button
                onClick={handlePrevious}
                className="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300 bg-neutral-200 hover:bg-[#C5075D] hover:text-white text-neutral-700 shrink-0"
                aria-label="Testimonio anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <div className="flex justify-center gap-1.5 flex-1 overflow-hidden">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`transition-all duration-300 rounded-full ${
                      index === currentIndex
                        ? 'bg-[#C5075D] w-6 h-2'
                        : 'bg-neutral-300 hover:bg-neutral-400 w-2 h-2'
                    }`}
                    aria-label={`Ir al testimonio ${index + 1}`}
                    aria-current={index === currentIndex ? 'true' : 'false'}
                  />
                ))}
              </div>

              <button
                onClick={handleNext}
                className="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300 bg-neutral-200 hover:bg-[#C5075D] hover:text-white text-neutral-700 shrink-0"
                aria-label="Siguiente testimonio"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
